import { Enums } from '@configur-tech/upit-core-types';
import {
  faArrowLeft,
  faChevronDown,
  faChevronUp,
  faCog,
  faInfoCircle,
  faShareAlt,
  faShieldAlt,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';
import usePortal from '../../../hooks/portal/UsePortal';
import { BackNavigationItem } from '../../../main/theme';
import {
  resetPortalStage,
  updateActiveProjectSubStage,
} from '../../../store/project-stage';
import {
  ConfigurationSubStage,
  ProjectConnectionPortalSubStage,
} from '../../../store/project-stage/initial-state';
import { RootState } from '../../../store/rootReducer';
import NavItem from '../../Navigation/NavItem';
import NavTitle from '../../Navigation/NavTitle';
import {
  NavigationAccordion,
  NavigationAccordionContent,
  NavigationAccordionTitle,
  NavigationNavItem,
} from '../ProjectItemNavigation/styled';

const NavIcons = {
  [ProjectConnectionPortalSubStage.NAME]: faInfoCircle,
  [ProjectConnectionPortalSubStage.CONNECTIONS]: faShareAlt,
  [ProjectConnectionPortalSubStage.ACCESS]: faCog,
};

const ProjectItemPortalNavigation: FC = () => {
  const dispatch = useDispatch();
  const themeContext = useContext(ThemeContext);
  const { portal } = usePortal();

  const projectStageState = useSelector(
    (state: RootState) => state.projectStage,
  );
  const projectStage = projectStageState.stages[Enums.ConnectionType.PORTAL];
  const activeSubStage = projectStage.activeSubStage;

  const [accordionGroups, setAccordionGroups] = useState<
    Record<string, boolean>
  >({ [ProjectConnectionPortalSubStage.CONFIGURATION]: true });

  return (
    <>
      <BackNavigationItem
        className={'back'}
        key={`portal-stage-back`}
        onClick={() => dispatch(resetPortalStage())}
        isActive={false}
      >
        <FontAwesomeIcon
          icon={faArrowLeft}
          color={themeContext.colors.system.white}
        />

        <NavTitle text={'Back To Portals'} />
      </BackNavigationItem>

      {Object.values(projectStage.subStages)
        .filter(
          (subStage) =>
            subStage.subStage !==
              ProjectConnectionPortalSubStage.CONFIGURATION &&
            subStage.subStage !== ProjectConnectionPortalSubStage.ACCESS,
        )
        .map((subStage) => {
          const subStageName =
            subStage.subStage as ProjectConnectionPortalSubStage;

          return (
            <NavItem
              disabled={
                subStageName === ProjectConnectionPortalSubStage.CONNECTIONS &&
                !portal?.name
              }
              key={`portal-stage-${subStageName}`}
              onClick={() =>
                dispatch(updateActiveProjectSubStage(subStageName))
              }
              isActive={activeSubStage === subStageName}
            >
              <FontAwesomeIcon
                icon={NavIcons[subStageName]}
                color={themeContext.colors.system.white}
              />

              <NavTitle text={subStage.title} />
            </NavItem>
          );
        })}

      {/* Configuration accordion nav */}
      {portal?._id && (
        <NavigationAccordion>
          <div key={`${ProjectConnectionPortalSubStage.CONFIGURATION}-0`}>
            <NavigationAccordionTitle
              active={
                !accordionGroups[ProjectConnectionPortalSubStage.CONFIGURATION]
              }
              index={0}
              onClick={() =>
                setAccordionGroups({
                  ...accordionGroups,
                  [ProjectConnectionPortalSubStage.CONFIGURATION]:
                    !accordionGroups[
                      ProjectConnectionPortalSubStage.CONFIGURATION
                    ],
                })
              }
            >
              <div style={{ width: '100%', display: 'flex' }}>
                <NavTitle
                  text={
                    projectStage.subStages.find(
                      (s) =>
                        s.subStage ===
                        ProjectConnectionPortalSubStage.CONFIGURATION,
                    )?.title || 'Config'
                  }
                />

                <FontAwesomeIcon
                  icon={
                    !accordionGroups[Enums.DatasetStage.CONFIGURATION]
                      ? faChevronUp
                      : faChevronDown
                  }
                  color={themeContext.colors.system.white}
                />
              </div>
            </NavigationAccordionTitle>

            <NavigationAccordionContent
              active={
                !accordionGroups[ProjectConnectionPortalSubStage.CONFIGURATION]
              }
            >
              <div>
                <NavigationNavItem
                  onClick={() => {
                    dispatch(
                      updateActiveProjectSubStage(
                        ProjectConnectionPortalSubStage.ACCESS,
                      ),
                    );
                  }}
                  isActive={
                    activeSubStage === ProjectConnectionPortalSubStage.ACCESS
                  }
                >
                  <FontAwesomeIcon
                    icon={faShieldAlt}
                    color={themeContext.colors.system.white}
                    style={{ marginRight: themeContext.margin.standard }}
                  />

                  <NavTitle
                    text={ProjectConnectionPortalSubStage.ACCESS}
                    isActive={true}
                  />
                </NavigationNavItem>
              </div>
              <div>
                <NavigationNavItem
                  onClick={() => {
                    dispatch(
                      updateActiveProjectSubStage(
                        ProjectConnectionPortalSubStage.CONFIGURATION,
                      ),
                    );
                  }}
                  isActive={
                    activeSubStage ===
                    ProjectConnectionPortalSubStage.CONFIGURATION
                  }
                >
                  <FontAwesomeIcon
                    icon={faCog}
                    color={themeContext.colors.system.white}
                    style={{ marginRight: themeContext.margin.standard }}
                  />

                  <NavTitle
                    text={ConfigurationSubStage.ADVANCED}
                    isActive={true}
                  />
                </NavigationNavItem>
              </div>
            </NavigationAccordionContent>
          </div>
        </NavigationAccordion>
      )}
    </>
  );
};

export default ProjectItemPortalNavigation;
